import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { toast } from "react-toastify";
import { useState, useEffect, useMemo } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import axios from "axios";

import Accordion from "../Accordion";
import ThicknessSelector from "../ThicknessSelector";

const ROOT = "https://bodiosyakdown.com/uploads";
const OPT  = "https://bodiosyakdown.com/uploads/optimized";

export function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize,  setSelectedSize]  = useState(null); 
  const [selectedImage, setSelectedImage] = useState(null);

  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper,   setMainSwiper]   = useState(null);

  const [defaultImages,   setDefaultImages]   = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);

  // ---------- Helper Functions ----------
  
  // Өнгө + size-аар зураг олох (channel_price массиваас зураг, channelPrice массиваас өнгө мэдээлэл)
  function pickCPImageByColorSize(prod, color, size) {
    if (!prod) return null;
    
    const c = (color || "").toString().trim();
    const s = (size || "").toString().trim();
    
    // channel_price массиваас зураг хайна (зурагтай)
    const channelPriceWithImages = prod.channel_price || [];
    
    let imageRec = null;
    
    // Эхлээд тохирох өнгө + size-аар зураг хайна
    if (c && s) {
      imageRec = channelPriceWithImages.find(r =>
        (r?.color ?? "").toString().trim() === c &&
        (r?.size ?? "").toString().trim() === s
      );
    }
    
    // Олдоогүй бол зөвхөн өнгөөр хайна
    if (!imageRec && c) {
      imageRec = channelPriceWithImages.find(r => 
        (r?.color ?? "").toString().trim() === c
      );
    }
    
    // Зураг олдсон бол буцаана
    if (imageRec?.image) {
      return imageRec.image.toString().trim();
    }
    
    return null;
  }

  // Боломжтой өнгөнүүдийг channelPrice массиваас авна
  function getParsedColors(product) {
    if (!product) return [];
    
    const channelPriceProcessed = product.channelPrice || [];
    
    return [...new Set(
      channelPriceProcessed
        .map(r => (r?.color ?? "").toString().trim())
        .filter(Boolean)
    )];
  }

  // ✅ ШИНЭ: Бүтээгдэхүүний нийт size-ууд авах (өнгөөс үл хамааран)
  function getAllSizes(prod) {
    if (!prod) return [];
    
    // Анхны size массив
    let direct = [];
    if (Array.isArray(prod.size)) {
      direct = prod.size;
    } else {
      try { 
        direct = JSON.parse(prod.size || "[]"); 
      } catch { 
        direct = []; 
      }
    }
    
    // channelPrice-аас бүх size-ууд цуглуулах
    const channelPriceProcessed = prod.channelPrice || [];
    const fromCP = [...new Set(
      channelPriceProcessed
        .map(r => (r?.size ?? "").toString().trim())
        .filter(Boolean)
    )];
    
    // Нийлүүлэх ба давхардсан утгыг арилгах
    return [...new Set([...direct, ...fromCP])];
  }

  // ✅ ШИНЭ: Тухайн өнгөнд боломжтой size-ууд (validation-д ашиглах)
  function getAvailableSizesForColor(prod, color) {
    if (!prod || !color) return getAllSizes(prod);
    
    const channelPriceProcessed = prod.channelPrice || [];
    
    const availableSizes = [...new Set(
      channelPriceProcessed
        .filter(r => (r?.color ?? "").toString().trim() === color)
        .map(r => (r?.size ?? "").toString().trim())
        .filter(Boolean)
    )];
    
    return availableSizes.length > 0 ? availableSizes : getAllSizes(prod);
  }

  // ✅ ШИНЭ: Size сонгогдох боломжтой эсэхийг шалгах
  function isSizeAvailable(prod, color, size) {
    if (!prod || !size) return false;
    
    // Өнгө сонгоогүй бол бүх size боломжтой
    if (!color) return true;
    
    const availableSizes = getAvailableSizesForColor(prod, color);
    return availableSizes.includes(size);
  }

  // ✅ ШИНЭ: Тухайн өнгө+size-ын stock авах
  function getStockForColorSize(prod, color, size) {
    if (!prod) return 0;
    
    const channelPriceProcessed = prod.channelPrice || [];
    
    // Өнгө ба size хоёулаа сонгогдсон бол тухайн варианты stock
    if (color && size) {
      const c = String(color).trim().toLowerCase();
      const s = String(size).trim().toLowerCase();
      
      const match = channelPriceProcessed.find(r => 
        String(r?.color || "").trim().toLowerCase() === c &&
        String(r?.size || "").trim().toLowerCase() === s
      );
      
      if (match) {
        return Number(match.stock) || 0;
      }
    }
    
    // Зөвхөн өнгө сонгогдсон бол тухайн өнгийн нийт stock
    if (color) {
      const c = String(color).trim().toLowerCase();
      return channelPriceProcessed
        .filter(r => String(r?.color || "").trim().toLowerCase() === c)
        .reduce((sum, r) => sum + (Number(r.stock) || 0), 0);
    }
    
    // Юу ч сонгоогүй бол бүтээгдэхүүний нийт stock
    return Number(prod.stock) || 0;
  }

  // Үнэ тооцоолох (channelPrice массиваас)
  function computeExactPairPrice(basePrice, product, { variantCode, color, size }) {
    if (!color || !size) return Number(basePrice ?? 0);
    
    const cSel = String(color).trim().toLowerCase();
    const sSel = String(size).trim().toLowerCase();
    const vSel = variantCode ? String(variantCode).trim().toLowerCase() : null;
    
    const channelPriceProcessed = product?.channelPrice || [];
    
    let found = null;
    for (const r of channelPriceProcessed) {
      const rColor = r.color ? String(r.color).trim().toLowerCase() : null;
      const rSize = r.size ? String(r.size).trim().toLowerCase() : null;
      const rVar = r.variantCode ? String(r.variantCode).trim().toLowerCase() : null;
      
      if (rColor === cSel && rSize === sSel) {
        if (vSel && rVar && rVar !== vSel) continue;
        const priceNum = Number(r.pairPrice);
        if (Number.isFinite(priceNum)) {
          found = priceNum;
          break;
        }
      }
    }
    
    return Number.isFinite(found) ? found : Number(basePrice ?? 0);
  }

  // Зургийн responsive sets үүсгэх
  function baseNoExt(name) {
    return typeof name === "string" ? name.replace(/\.[^.]+$/, "") : "";
  }
  
function buildSets(filename) {
  if (!filename) return null;
  
  // 🆕 Optimize-сон хувилбар байгаа эсэхийг шалгах
  if (product?.optimizedImages && Array.isArray(product.optimizedImages)) {
    const base = baseNoExt(filename);
    
    // Энэ filename-тай холбоотой optimize variants олох
    const lgVariant = product.optimizedImages.find(img => 
      img.variant === 'lg' && 
      (img.avif_url?.includes(base) || img.webp_url?.includes(base))
    );
    
    const smVariant = product.optimizedImages.find(img => 
      img.variant === 'sm' && 
      (img.avif_url?.includes(base) || img.webp_url?.includes(base))
    );
    
    // Optimize-сон хувилбар олдвол
    if (lgVariant || smVariant) {
      return {
        type: 'optimized',
        avifSet: [
          lgVariant?.avif_url ? `https://bodiosyakdown.com${lgVariant.avif_url} 1280w` : '',
          smVariant?.avif_url ? `https://bodiosyakdown.com${smVariant.avif_url} 640w` : ''
        ].filter(Boolean).join(', '),
        webpSet: [
          lgVariant?.webp_url ? `https://bodiosyakdown.com${lgVariant.webp_url} 1280w` : '',
          smVariant?.webp_url ? `https://bodiosyakdown.com${smVariant.webp_url} 640w` : ''
        ].filter(Boolean).join(', '),
        fallback: lgVariant?.webp_url 
          ? `https://bodiosyakdown.com${lgVariant.webp_url}` 
          : `https://bodiosyakdown.com/uploads/${filename}`,
      };
    }
  }
  
  // Fallback: оригинал зураг
  return {
    type: 'original',
    fallback: encodeURI(`https://bodiosyakdown.com/uploads/${filename}`),
  };
}

  // ---------- Data Fetching ----------
  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `https://admin.bodiosyakdown.com/api/products/${productId}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      const prod = res.data;

      // Parse color and size arrays
      try { 
        prod.color = Array.isArray(prod.color) ? prod.color : JSON.parse(prod.color || "[]"); 
      } catch { 
        prod.color = []; 
      }
      
      try { 
        prod.size = Array.isArray(prod.size) ? prod.size : JSON.parse(prod.size || "[]"); 
      } catch { 
        prod.size = []; 
      }

      // Parse channel price arrays
      if (Array.isArray(prod.channelPrice)) {
        // already parsed
      } else if (typeof prod.channel_price === "string") {
        try { 
          prod.channelPrice = JSON.parse(prod.channel_price) || []; 
        } catch { 
          prod.channelPrice = []; 
        }
      } else if (Array.isArray(prod.channel_price)) {
        prod.channelPrice = prod.channel_price;
      } else {
        prod.channelPrice = [];
      }

      setProduct(prod);
      
      // 🆕 Console-д шалгах (setProduct-ийн дараа)
      console.log('🔍 Product optimizedImages:', prod.optimizedImages);

      // Set up default images
      const imgs = prod.images ? (() => { 
        try { 
          return JSON.parse(prod.images); 
        } catch { 
          return []; 
        } 
      })() : [];
      
      const ordered = prod.mainImage 
        ? [prod.mainImage, ...imgs.filter(img => img !== prod.mainImage)] 
        : imgs;
      
      setDefaultImages(ordered);
      setDisplayedImages(ordered);
      
      if (ordered.length && !selectedImage) {
        setSelectedImage(ordered[0]);
      }

    } catch (e) {
      console.error("❌ Failed to fetch product:", e);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Image Management ----------
  function applyColorSizeImages(nextColor, nextSize) {
    if (!product) return;

    if (!nextColor) {
      // No color selected → show default images
      setDisplayedImages(defaultImages);
      if (defaultImages.length) {
        setSelectedImage(defaultImages[0]);
        queueMicrotask(() => mainSwiper?.slideTo(0));
      }
      return;
    }

    // Try to find color-specific image
    const cpImg = pickCPImageByColorSize(product, nextColor, nextSize);
    if (cpImg) {
      const merged = [cpImg, ...defaultImages.filter(x => x !== cpImg)];
      setDisplayedImages(merged);
      setSelectedImage(cpImg);
      queueMicrotask(() => mainSwiper?.slideTo(0));
    } else {
      setDisplayedImages(defaultImages);
      if (defaultImages.length) {
        setSelectedImage(defaultImages[0]);
        queueMicrotask(() => mainSwiper?.slideTo(0));
      }
    }
  }

  // ---------- Event Handlers ----------
  const handleSelectColor = (c) => {
    setSelectedColor(c);

    // Одоогийн size боломжтой эсэхийг шалгах
    const available = getAvailableSizesForColor(product, c);
    
    if (available.length && (!selectedSize || !available.includes(selectedSize))) {
      // Анхны боломжтой size сонгох
      setSelectedSize(available[0]);
      applyColorSizeImages(c, available[0]);
    } else {
      applyColorSizeImages(c, selectedSize);
    }
  };

  // ✅ ШИНЭ: Size сонгох handler
  const handleSelectSize = (size) => {
    // Хэрэв өнгө сонгосон бол тухайн өнгөнд боломжтой эсэхийг шалгах
    if (selectedColor) {
      const available = isSizeAvailable(product, selectedColor, size);
      if (!available) {
        toast.warning(`Size ${size} is not available for ${selectedColor}`);
        return;
      }
    }
    
    setSelectedSize(size);
  };

  // Update images when size changes (if color is selected)
  useEffect(() => {
    if (!product || !selectedColor) return;
    applyColorSizeImages(selectedColor, selectedSize);
  }, [selectedSize, product, selectedColor]);

  // ---------- Computed Values ----------
  // ✅ ШИНЭ: Бүх size-ууд харуулах
  const allSizes = useMemo(() => {
    return getAllSizes(product);
  }, [product]);
  
  // ✅ ШИНЭ: Боломжтой size-ууд (validation-д)
  const availableSizes = useMemo(() => {
    return getAvailableSizesForColor(product, selectedColor || undefined);
  }, [product, selectedColor]);

  const parsedColors = useMemo(() => {
    return getParsedColors(product);
  }, [product]);

  const shownPrice = useMemo(() => {
    if (!product) return 0;
    return computeExactPairPrice(
      Number(product.price),
      product,
      {
        variantCode: undefined,
        color: selectedColor || undefined,
        size: selectedSize || undefined,
      }
    );
  }, [product, selectedColor, selectedSize]);

  // ✅ ШИНЭ: Одоогийн stock тооцоолох
  const currentStock = useMemo(() => {
    return getStockForColorSize(product, selectedColor, selectedSize);
  }, [product, selectedColor, selectedSize]);

  // Color class mapping
  const getColorClass = (color) => {
    const colorMap = {
      "Black": "bg-black",
      "White": "bg-white",
      "White yak": "bg-gray-100", 
      "Brown yak": "bg-yellow-800",
      "Silver yak": "bg-gray-400",
      "Beige": "bg-yellow-100",
      "Blue": "bg-blue-200",
      "Red": "bg-red-500",
      "Green": "bg-green-500",
      "Yellow": "bg-yellow-400",
      "Gray": "bg-gray-400",
      "Orange": "bg-orange-400",
      "Purple": "bg-purple-400",
      "Brown": "bg-yellow-800",
      "Pink": "bg-pink-300",
      "Navy Blue": "bg-blue-900",
      "Teal": "bg-teal-400",
      "Maroon": "bg-red-900",
      "Olive Green": "bg-green-700",
      "Sky Blue": "bg-sky-300",
      "Dark Brown": "bg-yellow-900",
      "Light Gray": "bg-gray-200",
      "Lavender": "bg-purple-200"
    };
    return colorMap[color] || "bg-gray-300";
  };

  // ---------- Cart Functions ----------
  const handleAddToCart = () => {
    const needColor = parsedColors.length > 0;
    
    // ✅ ШИНЭ: Size заавал сонгох шаардлагатай
    if (!selectedSize) {
      toast.error("Хэмжээ сонгоно уу.");
      return;
    }
    
    if (needColor && !selectedColor) {
      toast.error("Өнгө сонгоно уу.");
      return;
    }
    
    // ✅ Stock шалгах
    if (currentStock === 0) {
      toast.error("Энэ хослол дууссан байна.");
      return;
    }
    
    addToCart({
      id: product.id,
      title: product.name,
      price: shownPrice,
      image: selectedImage ? `${ROOT}/${selectedImage}` : "",
      quantity: 1,
      color: selectedColor || null,
      size: selectedSize || null,
    });
    toast.success("🛒 Added to cart!");
  };

  // ---------- Render ----------
  if (loading) return <p className="text-center py-20">Loading...</p>;
  if (!product) return <p className="text-center py-20">Product not found</p>;

  const MAIN_ASPECT = "aspect-[3/4]";

  return (
    <div className="max-w-[1600px] mx-auto px-2 py-4">
      <div className="w-full flex flex-col md:flex-row gap-10 mt-28 font-suisse justify-center">
        {/* Left Side - Images */}
        <div className="w-full md:w-[49.5%]">
          <div className={`overflow-hidden ${MAIN_ASPECT}`}>
            <Swiper
              modules={[Navigation, Thumbs]}
              onSwiper={setMainSwiper}
              navigation
              thumbs={{ 
                swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null 
              }}
              className="h-full"
            >
         {displayedImages.map((img, i) => {
            const sets = buildSets(img);
            
            return (
              <SwiperSlide key={i}>
                {sets?.type === 'optimized' ? (
                  <picture>
                    <source type="image/avif" srcSet={sets.avifSet} sizes="50vw" />
                    <source type="image/webp" srcSet={sets.webpSet} sizes="50vw" />
                    <img
                      src={sets.fallback}
                      alt={`${product.name} ${i + 1}`}
                      className="object-cover w-full h-full"
                      loading={i === 0 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  </picture>
                ) : (
                  <img
                    src={sets?.fallback || encodeURI(`${ROOT}/${img}`)}
                    alt={`${product.name} ${i + 1}`}
                    className="object-cover w-full h-full"
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                  />
                )}
              </SwiperSlide>
            );
          })}
            </Swiper>
          </div>
        </div>

        {/* Right Side - Product Details */}
        <div className="md:w-1/2">
          <button 
            onClick={() => navigate(-1)} 
            className="text-sm text-gray-600 mb-4 hover:underline"
          >
            &larr; BACK
          </button>

          <h1 className="text-xl font-semibold mb-3">{product.name}</h1>
          <p className="text-gray-600 mb-4">{product.description}</p>

          <p className="text-lg font-600 mb-6">
            {new Intl.NumberFormat("en-US", { 
              style: "currency", 
              currency: "USD" 
            }).format(shownPrice)}
          </p>

          {/* ✅ DYNAMIC STOCK DISPLAY */}
          <p className={`text-sm mb-6 ${
            currentStock === 0 
              ? "text-red-500" 
              : currentStock < 5 
                ? "text-orange-500" 
                : "text-green-600"
          }`}>
            {currentStock > 0 
              ? `${currentStock} in stock${
                  selectedColor && selectedSize 
                    ? ` (${selectedColor} / ${selectedSize})` 
                    : selectedColor 
                      ? ` (${selectedColor})` 
                      : ""
                }` 
              : "Out of stock"
            }
          </p>

          {/* Color Selection */}
          {parsedColors.length > 0 && (
            <div className="mb-6">
              <p className="font-medium mb-2">
                COLOR: {selectedColor || "Select"}
              </p>
              <div className="flex gap-2 flex-wrap">
                {parsedColors.map(c => (
                  <div
                    key={c}
                    onClick={() => handleSelectColor(c)}
                    className={`w-8 h-8 border-2 cursor-pointer flex items-center justify-center ${
                      selectedColor === c 
                        ? "ring-2 ring-black ring-offset-1" 
                        : "hover:ring-1 hover:ring-gray-400"
                    } ${getColorClass(c)}`}
                    title={c}
                  >
                    {c === "White" || c === "White yak" ? (
                      <div className="w-6 h-6 border border-gray-300"></div>
                    ) : null}
                  </div>
                ))}
              </div>
              {selectedColor && (
                <p className="text-xs text-gray-500 mt-1">{selectedColor}</p>
              )}
            </div>
          )}

          {/* ✅ SIZE SELECTION - ЗАСВАРЛАСАН */}
          <div className="mb-6">
            <p className="text-sm mb-2 font-medium">SIZE:</p>
            <div className="flex gap-3 flex-wrap">
              {allSizes.map(size => {
                const isAvailable = isSizeAvailable(product, selectedColor, size);
                const isSelected = selectedSize === size;
                
                return (
                  <span
                    key={size}
                    onClick={() => handleSelectSize(size)}
                    className={`text-sm px-4 py-2 border rounded cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-black text-white border-black"
                        : isAvailable
                          ? "hover:bg-gray-100 border-gray-300"
                          : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                    }`}
                    title={!isAvailable && selectedColor 
                      ? `Not available for ${selectedColor}` 
                      : undefined
                    }
                  >
                    {size}
                    {!isAvailable && selectedColor && (
                      <span className="ml-1 text-xs"></span>
                    )}
                  </span>
                );
              })}
              {!allSizes?.length && (
                <span className="text-gray-400">No sizes available</span>
              )}
            </div>
            
            {/* ✅ Хэрэв өнгө сонгосон бол мэдээлэл харуулах */}
            {selectedColor && (
              <p className="text-xs text-gray-500 mt-2">
                Available sizes for {selectedColor}: {availableSizes.join(", ") || "None"}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col">
            <button
              onClick={handleAddToCart}
              disabled={!selectedSize || currentStock === 0}
              className={`w-[220px] py-3 px-6 rounded mb-4 transition-all duration-150 font-medium ${
                !selectedSize || currentStock === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-black text-white hover:bg-gray-800"
              }`}
            >
              ADD TO CART
            </button>

            <button
              onClick={() => navigate("/cart")}
              className="w-[220px] text-sm py-3 px-6 rounded mb-4 bg-white border border-black text-black hover:bg-gray-100 transition-all duration-150"
            >
              GO TO CART
            </button>
          </div>

          {/* Product Features */}
          <div className="flex flex-wrap gap-6 text-sm text-gray-600 mt-6">
            <p className="flex items-center gap-2">
              <span className="text-black text-sm">✔</span> 
              WORLDWIDE SHIPPING
            </p>
            <p className="flex items-center gap-2">
              <span className="text-black text-sm">✔</span> 
              HIGH QUALITY CASHMERE
            </p>
            <p className="flex items-center gap-2">
              <span className="text-black text-sm">✔</span> 
              FAST DELIVERY
            </p>
          </div>

          {/* Product Information Accordion */}
          <div className="mt-12 border-t pt-6 w-full">
            <div className="max-w-2xl w-full space-y-4">
              <Accordion title="Thickness">
                <div>
                  <ThicknessSelector value={product.thickness} />
                </div>
              </Accordion>
              
              <Accordion title="Material">
                {product.material || "100% Cashmere"}
              </Accordion>
              
              <Accordion title="Care Instructions">
                {product.care_instructions || "Dry clean only. Do not bleach."}
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mb-4 mt-16">
        <div className="columns-1 md:columns-2 [column-fill:_balance] gap-4">
          {displayedImages.map((img, i) => {
            const sets = buildSets(img);
            
            return (
              <div key={i} className="break-inside-avoid mb-4">
                {sets?.type === 'optimized' ? (
                  <picture>
                    <source type="image/avif" srcSet={sets.avifSet} />
                    <source type="image/webp" srcSet={sets.webpSet} />
                    <img
                      src={sets.fallback}
                      alt={`${product.name} gallery ${i + 1}`}
                      loading="lazy"
                      className="w-full h-auto object-cover rounded"
                    />
                  </picture>
                ) : (
                  <img
                    src={sets?.fallback || encodeURI(`${ROOT}/${img}`)}
                    alt={`${product.name} gallery ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto object-cover rounded"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}